import React, { useEffect, useMemo, useState } from "react";

import { AlertTriangle, CheckCircle, Database, RefreshCw } from "lucide-react";

import Badge from "../../Components/Badge";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import SearchInput from "../../Components/Search/SearchInput";
import Select from "../../Components/Select";
import Table from "../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchBusinessHealthBriefingAccounts,
  syncBusinessHealthBriefings,
} from "../../Store/Reducers/AdminSlice";
import "./style.scss";

export interface OrganizationFromAdmin {
  tenant_id?: string;
  organization: string;
  tenant_schema_name?: string;
}

interface SyncTenantResult {
  schema?: string;
  success?: boolean;
  date?: string;
  requested?: number;
  stored?: number;
  skipped_incomplete?: number;
  missing?: number;
  error?: string;
}

interface BhAccountRow {
  id: string;
  name: string;
  stored?: boolean;
  digest_date?: string | null;
}

interface AdminBusinessHealthBriefingContainerProps {
  organizations: OrganizationFromAdmin[];
}

function formatBriefingDate(raw?: string | null): string {
  if (!raw) return "—";
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return raw;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

const AdminBusinessHealthBriefingContainer: React.FC<
  AdminBusinessHealthBriefingContainerProps
> = ({ organizations }) => {
  const dispatch = useAppDispatch();
  const {
    bhBriefingSyncResult: result,
    bhBriefingSyncLoading: syncing,
    bhBriefingSyncError: error,
    bhBriefingAccounts,
  } = useAppSelector((state: { admin: any }) => state.admin);

  const [selectedOrg, setSelectedOrg] = useState("all");
  const [accountQuery, setAccountQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSyncAll, setConfirmSyncAll] = useState(false);

  const orgOptions = useMemo(() => {
    const options = [{ label: "All organizations", value: "all" }];
    for (const org of organizations || []) {
      const id = String(org.tenant_id || "").trim();
      if (!id) continue;
      options.push({
        label: org.organization || org.tenant_schema_name || id,
        value: id,
      });
    }
    return options;
  }, [organizations]);

  const accounts: BhAccountRow[] = useMemo(
    () =>
      (bhBriefingAccounts?.accounts || []).map((row: BhAccountRow) => ({
        id: String(row.id || "").trim(),
        name: row.name || "Unknown",
        stored: Boolean(row.stored),
        digest_date: row.digest_date || null,
      })),
    [bhBriefingAccounts?.accounts]
  );

  const filteredAccounts = useMemo(() => {
    const needle = accountQuery.trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter(
      row =>
        row.name.toLowerCase().includes(needle) ||
        row.id.toLowerCase().includes(needle)
    );
  }, [accounts, accountQuery]);

  useEffect(() => {
    if (selectedOrg === "all") {
      setSelectedIds(new Set());
      setAccountQuery("");
      return;
    }
    setSelectedIds(new Set());
    setAccountQuery("");
    void dispatch(fetchBusinessHealthBriefingAccounts(selectedOrg) as any);
  }, [dispatch, selectedOrg]);

  useEffect(() => {
    const valid = new Set(accounts.map(row => row.id).filter(Boolean));
    setSelectedIds(prev => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      return changed || next.size !== prev.size ? next : prev;
    });
  }, [accounts]);

  const tenantResults: SyncTenantResult[] = Array.isArray(result?.results)
    ? result.results
    : [];

  const selectedLabel =
    selectedOrg === "all"
      ? "all organizations"
      : orgOptions.find(o => o.value === selectedOrg)?.label || selectedOrg;

  const selectedAccountIds = useMemo(
    () => accounts.filter(row => selectedIds.has(row.id)).map(row => row.id),
    [accounts, selectedIds]
  );

  const allFilteredSelected =
    filteredAccounts.length > 0 &&
    filteredAccounts.every(row => selectedIds.has(row.id));
  const someFilteredSelected = filteredAccounts.some(row =>
    selectedIds.has(row.id)
  );

  const openConfirm = (syncAll: boolean) => {
    setConfirmSyncAll(syncAll);
    setConfirmOpen(true);
  };

  const handleSync = async () => {
    setConfirmOpen(false);
    const payload: {
      syncAll?: boolean;
      orgId?: string;
      accountIds?: string[];
    } = {};
    if (confirmSyncAll || selectedOrg === "all") {
      payload.syncAll = true;
    } else {
      payload.orgId = selectedOrg;
      if (selectedAccountIds.length > 0) {
        payload.accountIds = selectedAccountIds;
      }
    }
    await dispatch(syncBusinessHealthBriefings(payload) as any);
    if (selectedOrg !== "all") {
      void dispatch(fetchBusinessHealthBriefingAccounts(selectedOrg) as any);
    }
  };

  const syncButtonLabel = (() => {
    if (selectedOrg === "all") return "Sync all organizations";
    if (selectedAccountIds.length > 0) {
      return `Sync ${selectedAccountIds.length} selected`;
    }
    return "Sync selected organization";
  })();

  const confirmMessage = (() => {
    if (confirmSyncAll || selectedOrg === "all") {
      return "Sync complete Business Health digests for all active organizations?";
    }
    if (selectedAccountIds.length > 0) {
      return `Sync complete Business Health digests for ${selectedAccountIds.length} selected account${
        selectedAccountIds.length === 1 ? "" : "s"
      } in ${selectedLabel}?`;
    }
    return `Sync complete Business Health digests for all accounts in ${selectedLabel}?`;
  })();

  const accountColumns = useMemo(
    () => [
      {
        key: "select",
        title: (
          <input
            type="checkbox"
            className="admin-bh-briefing__row-check"
            checked={allFilteredSelected}
            ref={el => {
              if (el) {
                el.indeterminate = someFilteredSelected && !allFilteredSelected;
              }
            }}
            onChange={e => {
              const checked = e.target.checked;
              setSelectedIds(prev => {
                const next = new Set(prev);
                filteredAccounts.forEach(row => {
                  if (checked) next.add(row.id);
                  else next.delete(row.id);
                });
                return next;
              });
            }}
            disabled={syncing || filteredAccounts.length === 0}
            aria-label="Select all visible accounts"
            onClick={e => e.stopPropagation()}
          />
        ),
        width: "44px",
        render: (_value: unknown, record: BhAccountRow) => (
          <input
            type="checkbox"
            className="admin-bh-briefing__row-check"
            checked={selectedIds.has(record.id)}
            onChange={e => {
              const checked = e.target.checked;
              setSelectedIds(prev => {
                const next = new Set(prev);
                if (checked) next.add(record.id);
                else next.delete(record.id);
                return next;
              });
            }}
            disabled={syncing || !record.id}
            aria-label={`Select ${record.name || record.id}`}
            onClick={e => e.stopPropagation()}
          />
        ),
      },
      {
        key: "name",
        title: "Account",
        render: (value: string) => value || "—",
      },
      {
        key: "id",
        title: "Account ID",
        render: (value: string) => (
          <span className="admin-bh-briefing__mono">{value || "—"}</span>
        ),
      },
      {
        key: "stored",
        title: "Stored digest",
        render: (_value: unknown, record: BhAccountRow) =>
          record.stored ? (
            <Badge type="success" label="Stored" />
          ) : (
            <Badge type="warning" label="Not stored" />
          ),
      },
      {
        key: "digest_date",
        title: "Digest date",
        render: (value: string) => formatBriefingDate(value),
      },
    ],
    [
      allFilteredSelected,
      filteredAccounts,
      selectedIds,
      someFilteredSelected,
      syncing,
    ]
  );

  const resultColumns = [
    {
      key: "schema",
      title: "Tenant schema",
      render: (value: string) => (
        <span className="admin-bh-briefing__mono">{value || "—"}</span>
      ),
    },
    {
      key: "success",
      title: "Status",
      render: (value: boolean) => (
        <Badge
          type={value ? "success" : "danger"}
          label={value ? "OK" : "Failed"}
        />
      ),
    },
    {
      key: "date",
      title: "Briefing date",
      render: (value: string) => formatBriefingDate(value),
    },
    {
      key: "requested",
      title: "Requested",
      render: (value: number) => value ?? 0,
    },
    {
      key: "stored",
      title: "Stored (complete)",
      render: (value: number) => value ?? 0,
    },
    {
      key: "skipped_incomplete",
      title: "Skipped (partial/failed)",
      render: (value: number) => value ?? 0,
    },
    {
      key: "missing",
      title: "Missing in X-Factor",
      render: (value: number) => value ?? 0,
    },
    {
      key: "error",
      title: "Notes",
      render: (value: string) =>
        value ? (
          <span className="admin-bh-briefing__error">{value}</span>
        ) : (
          <span className="admin-bh-briefing__muted">—</span>
        ),
    },
  ];

  return (
    <div className="admin-bh-briefing">
      <div className="admin-bh-briefing__hero">
        <div className="admin-bh-briefing__hero-title-row">
          <Database size={28} />
          <div>
            <h2 className="admin-bh-briefing__title">
              Business Health Digests
            </h2>
            <p className="admin-bh-briefing__subtitle">
              Pull complete X-Factor digests into ExecBridge. One week is
              retained per account. Partial or failed digests are never stored.
              This does not regenerate briefings — use IB Health Check to
              generate or repair.
            </p>
          </div>
        </div>
      </div>

      <div className="admin-bh-briefing__card">
        <h3 className="admin-bh-briefing__card-title">Sync digests</h3>
        <p className="admin-bh-briefing__card-copy">
          Sync all organizations, one organization, or selected accounts within
          an organization. Uses the default briefing date (last Sunday PST).
        </p>

        <div className="admin-bh-briefing__steps">
          <div className="admin-bh-briefing__step">
            <strong>Pull from X-Factor</strong>
            <p>Fetch digests for the selected week.</p>
          </div>
          <div className="admin-bh-briefing__step">
            <strong>Store complete only</strong>
            <p>
              Incomplete digests are skipped. Existing complete rows are kept.
            </p>
          </div>
        </div>

        <div className="admin-bh-briefing__run-row">
          <div className="admin-bh-briefing__select">
            <Select
              name="bhBriefingOrg"
              label="Organization"
              value={selectedOrg}
              onChange={e => setSelectedOrg(String(e.target.value))}
              options={orgOptions}
              isShowPlaceHolder={false}
              disabled={syncing}
            />
          </div>
          <div className="admin-bh-briefing__actions">
            <Button
              variant="primary"
              icon={<RefreshCw size={16} />}
              onClick={() => openConfirm(selectedOrg === "all")}
              disabled={
                syncing ||
                (selectedOrg !== "all" &&
                  (bhBriefingAccounts?.loading ||
                    Boolean(bhBriefingAccounts?.error)))
              }
              loading={syncing}
            >
              {syncButtonLabel}
            </Button>
            {selectedOrg !== "all" && (
              <Button
                variant="secondary"
                onClick={() => openConfirm(true)}
                disabled={syncing}
              >
                Sync all instead
              </Button>
            )}
          </div>
        </div>

        {selectedOrg !== "all" ? (
          <div className="admin-bh-briefing__accounts">
            <div className="admin-bh-briefing__accounts-head">
              <div>
                <h4 className="admin-bh-briefing__accounts-title">
                  Select accounts
                </h4>
                <p className="admin-bh-briefing__accounts-hint">
                  Choose accounts to sync, or leave none selected to sync the
                  full organization.
                </p>
              </div>
              <span className="admin-bh-briefing__bulk-count">
                {selectedAccountIds.length} selected
                {accounts.length > 0 ? ` of ${accounts.length}` : ""}
              </span>
            </div>

            {bhBriefingAccounts?.loading ? (
              <p className="admin-bh-briefing__accounts-hint">
                Loading accounts…
              </p>
            ) : bhBriefingAccounts?.error ? (
              <p className="admin-bh-briefing__error">
                {bhBriefingAccounts.error}
              </p>
            ) : accounts.length === 0 ? (
              <p className="admin-bh-briefing__accounts-hint">
                No active accounts found for this organization.
              </p>
            ) : (
              <>
                <div className="admin-bh-briefing__filters">
                  <div className="admin-bh-briefing__search">
                    <SearchInput
                      placeholder="Search account name or ID"
                      value={accountQuery}
                      onChange={e => setAccountQuery(e.target.value)}
                      rounded="normal"
                    />
                  </div>
                </div>
                <Table
                  columns={accountColumns}
                  data={filteredAccounts}
                  rowKey={(row: BhAccountRow) => row.id}
                  pagination
                  defaultRowsPerPage={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  scrollable
                  maxHeight="320px"
                  emptyContent="No accounts match that search."
                />
              </>
            )}
          </div>
        ) : null}

        <p className="admin-bh-briefing__meta">
          Only complete digests are stored. Always-On, Portfolio, and Signals
          read from ExecBridge after a successful sync.
        </p>
        {error ? <p className="admin-bh-briefing__error">{error}</p> : null}
      </div>

      {result ? (
        <div className="admin-bh-briefing__card">
          <h3 className="admin-bh-briefing__card-title">Last sync result</h3>
          <div className="admin-bh-briefing__summary">
            <span className="admin-bh-briefing__chip">
              Tenants: {result.processed ?? tenantResults.length}
            </span>
            <span className="admin-bh-briefing__chip admin-bh-briefing__chip--ok">
              <CheckCircle size={14} /> Stored: {result.stored ?? 0}
            </span>
            <span
              className={`admin-bh-briefing__chip${
                (result.failures ?? 0) > 0
                  ? " admin-bh-briefing__chip--bad"
                  : ""
              }`}
            >
              {(result.failures ?? 0) > 0 ? <AlertTriangle size={14} /> : null}{" "}
              Failures: {result.failures ?? 0}
            </span>
          </div>
          <Table
            columns={resultColumns}
            data={tenantResults}
            rowKey={(row: SyncTenantResult, index?: number) =>
              `${row.schema || "tenant"}-${index ?? 0}`
            }
            pagination={tenantResults.length > 10}
            defaultRowsPerPage={10}
            emptyContent="No tenant results."
          />
        </div>
      ) : null}

      <CommonModal
        isOpen={confirmOpen}
        toggle={() => {
          if (!syncing) setConfirmOpen(false);
        }}
        title="Confirm digest sync"
        size="md"
        footer={
          <>
            <Button
              variant="gray"
              onClick={() => setConfirmOpen(false)}
              disabled={syncing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSync}
              loading={syncing}
              disabled={syncing}
            >
              Confirm sync
            </Button>
          </>
        }
      >
        <p>{confirmMessage}</p>
        <p className="admin-bh-briefing__card-copy">
          Briefing date: default (last Sunday PST). Partial or failed digests
          will not be stored. This does not regenerate briefings in X-Factor.
        </p>
      </CommonModal>
    </div>
  );
};

export default AdminBusinessHealthBriefingContainer;
